import { useEffect, useRef, useState } from "react";
import { AlertTriangle, Sparkles } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Textarea } from "@/components/ui/Select";
import { Button } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import {
  draftPrompt,
  PromptBuilderQuotaError,
  type DraftPromptRequest,
  type DraftPromptResult,
  type RefinementTurn,
} from "@/api/promptBuilder";
import { ApiError } from "@/api/client";
import type { UpsertReportTemplatePayload } from "@/api/reportTemplates";

const MIN_DESCRIPTION_CHARS = 20;

/**
 * Optimistic cap; the canonical value lives server-side in
 * PromptBuilderOptions.MaxRefinementTurns. We surface a soft hint in the
 * UI when the admin is approaching it; the backend enforces hard.
 */
const SOFT_REFINEMENT_LIMIT_HINT = 10;

/** Rotated under the spinner during the Generating state — purely cosmetic. */
const GENERATING_STATUS_MESSAGES = [
  "Reviewing your form schema…",
  "Drafting the prompt…",
  "Polishing tone and structure…",
  "Checking placeholders…",
];

interface PromptBuilderModalProps {
  /** Existing template ID, or `null` for the new-template flow (no ID yet). */
  templateId: string | null;
  /** Parent form snapshot — supplies category, master-doc flag, and form schema. */
  form: UpsertReportTemplatePayload;
  /** Called with the drafted text when the admin clicks "Use this". */
  onAcceptDraft: (draftedPromptBody: string) => void;
  onClose: () => void;
}

type ModalState = "empty" | "generating" | "result";

/**
 * AI Prompt Builder v1.0 §5b — three-state modal that drafts a prompt
 * body via Claude, supports natural-language refinement, and copies the
 * accepted draft into the parent template form.
 *
 * The conversation history lives ONLY in modal-local state per spec §6
 * ("NEVER: Persist the draft itself to the database"). Closing the modal
 * discards everything; reopening starts fresh.
 */
export function PromptBuilderModal({
  templateId,
  form,
  onAcceptDraft,
  onClose,
}: PromptBuilderModalProps) {
  const { toast } = useToast();

  const [modalState, setModalState] = useState<ModalState>("empty");
  const [description, setDescription] = useState("");
  const [refinementHistory, setRefinementHistory] = useState<RefinementTurn[]>([]);
  const [refineFeedback, setRefineFeedback] = useState("");

  // Last successful draft + its metadata. `null` until the first generate.
  const [lastResult, setLastResult] = useState<DraftPromptResult | null>(null);

  // Backend quota counters — surfaced after the first successful response.
  // We don't pre-fetch quota on modal open (anti-goal: "Do NOT call the
  // backend during the initial modal open"). Until the first call lands,
  // both fields are null and the modal renders "—".
  const [draftsUsedToday, setDraftsUsedToday] = useState<number | null>(null);
  const [dailyDraftLimit, setDailyDraftLimit] = useState<number | null>(null);

  // 4xx / 5xx error message rendered inline. Cleared on every new attempt.
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Quota-exceeded banner shown atop the result pane when status === 429.
  // Persists alongside the existing draft so the admin can still "Use this".
  const [quotaBanner, setQuotaBanner] = useState<{
    message: string;
    retryAfterSeconds: number | null;
  } | null>(null);

  // Cosmetic rotating status message during Generating state.
  const [statusIndex, setStatusIndex] = useState(0);

  // Active in-flight request controller, so Cancel can abort.
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (modalState !== "generating") return;
    setStatusIndex(0);
    const interval = window.setInterval(() => {
      setStatusIndex((i) => (i + 1) % GENERATING_STATUS_MESSAGES.length);
    }, 2000);
    return () => window.clearInterval(interval);
  }, [modalState]);

  // Cancel any in-flight request when the modal unmounts. Closing the
  // modal also discards everything else (component unmounts; state goes).
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const atQuota =
    draftsUsedToday !== null &&
    dailyDraftLimit !== null &&
    draftsUsedToday >= dailyDraftLimit;

  const draftsRemaining =
    draftsUsedToday !== null && dailyDraftLimit !== null
      ? Math.max(0, dailyDraftLimit - draftsUsedToday)
      : null;

  const generateDisabled =
    description.trim().length < MIN_DESCRIPTION_CHARS ||
    atQuota ||
    modalState === "generating";

  const refineDisabled =
    refineFeedback.trim().length === 0 || atQuota || modalState === "generating";

  const callBackend = async (history: RefinementTurn[] | null) => {
    setErrorMessage(null);

    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setModalState("generating");

    const body: DraftPromptRequest = {
      description: description.trim(),
      category: form.category,
      hasMasterDocument: !!form.masterDocumentId,
      formSections: form.sections.map((s) => ({
        title: s.title,
        questions: s.questions.map((q) => ({
          questionText: q.questionText,
          questionType: q.questionType,
        })),
      })),
      refinementHistory: history && history.length > 0 ? history : null,
    };

    try {
      const result = await draftPrompt(templateId, body, ctrl.signal);
      // If the caller aborted while we were waiting, swallow.
      if (ctrl.signal.aborted) return;

      setLastResult(result);
      setDraftsUsedToday(result.draftsUsedToday);
      setDailyDraftLimit(result.dailyDraftLimit);
      setQuotaBanner(null);
      setModalState("result");
    } catch (err) {
      // Aborted by the user via Cancel — just go back to the prior state
      // without surfacing an error.
      if (
        err instanceof DOMException && err.name === "AbortError"
      ) {
        return;
      }
      handleApiError(err);
    }
  };

  const handleApiError = (err: unknown) => {
    if (err instanceof PromptBuilderQuotaError) {
      // 429: Drop into Result state so the admin can still "Use this" on an
      // existing draft. Fields draftsUsedToday / dailyDraftLimit may already
      // be populated from a prior successful call; if not, infer from the
      // error message and keep the banner up.
      const remainingAtReset =
        err.retryAfterSeconds !== null
          ? Math.round(err.retryAfterSeconds / 60)
          : null;
      const banner = remainingAtReset !== null
        ? `Daily limit reached. Resets in ~${remainingAtReset} minute${remainingAtReset === 1 ? "" : "s"} (UTC midnight). The existing draft is still usable.`
        : "Daily limit reached. Resets at midnight UTC. The existing draft is still usable.";
      setQuotaBanner({ message: banner, retryAfterSeconds: err.retryAfterSeconds });
      setModalState(lastResult ? "result" : "empty");
      if (!lastResult) {
        setErrorMessage(banner);
      }
      return;
    }

    if (err instanceof ApiError) {
      if (err.status === 403) {
        toast({
          tone: "error",
          message: "You don't have permission to use this feature.",
        });
        window.setTimeout(() => onClose(), 3000);
        return;
      }
      if (err.status >= 500) {
        setErrorMessage(
          "The drafting service is temporarily unavailable. Try again in a moment, or write the prompt manually.",
        );
        setModalState(lastResult ? "result" : "empty");
        return;
      }
      // 400 (validation) or other 4xx — show the message inline.
      const friendly =
        err.errors[0] ?? err.message ?? "We couldn't draft that prompt. Try rephrasing your description and click Generate again.";
      setErrorMessage(friendly);
      setModalState(lastResult ? "result" : "empty");
      return;
    }

    setErrorMessage(
      err instanceof Error
        ? err.message
        : "Unexpected error. Try again, or write the prompt manually.",
    );
    setModalState(lastResult ? "result" : "empty");
  };

  const handleGenerate = () => {
    if (generateDisabled) return;
    setRefinementHistory([]);
    setLastResult(null);
    setQuotaBanner(null);
    void callBackend(null);
  };

  const handleRefine = () => {
    if (refineDisabled || !lastResult) return;
    const turn: RefinementTurn = {
      adminFeedback: refineFeedback.trim(),
      assistantDraft: lastResult.draftedPromptBody,
    };
    const nextHistory = [...refinementHistory, turn];
    setRefinementHistory(nextHistory);
    setRefineFeedback("");
    void callBackend(nextHistory);
  };

  const handleCancelInFlight = () => {
    abortRef.current?.abort();
    abortRef.current = null;
    // If we have a prior draft go back to viewing it; otherwise back to
    // the description input.
    setModalState(lastResult ? "result" : "empty");
  };

  const handleAccept = () => {
    if (!lastResult) return;
    onAcceptDraft(lastResult.draftedPromptBody);
    onClose();
    toast({
      tone: "success",
      message: "Prompt body updated. Don't forget to Save the template.",
    });
  };

  const descriptionLabel =
    "Describe what you want this report to do, in plain English.";

  // Footer changes per state. The Modal component wires the close
  // button + Escape; we provide the action buttons here.
  let footer: React.ReactNode;
  if (modalState === "generating") {
    footer = (
      <Button variant="secondary" type="button" onClick={handleCancelInFlight}>
        Cancel
      </Button>
    );
  } else if (modalState === "result") {
    footer = (
      <>
        <QuotaIndicator
          draftsUsedToday={draftsUsedToday}
          draftsRemaining={draftsRemaining}
        />
        <Button variant="secondary" type="button" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="secondary"
          type="button"
          onClick={handleRefine}
          disabled={refineDisabled}
          title={atQuota ? "Daily limit reached. Resets at midnight UTC." : undefined}
        >
          Refine
        </Button>
        <Button type="button" onClick={handleAccept} disabled={!lastResult}>
          Use this
        </Button>
      </>
    );
  } else {
    footer = (
      <>
        <QuotaIndicator
          draftsUsedToday={draftsUsedToday}
          draftsRemaining={draftsRemaining}
        />
        <Button variant="secondary" type="button" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="button"
          onClick={handleGenerate}
          disabled={generateDisabled}
          title={
            atQuota
              ? "Daily limit reached. Resets at midnight UTC."
              : description.trim().length < MIN_DESCRIPTION_CHARS
                ? `Add at least ${MIN_DESCRIPTION_CHARS - description.trim().length} more character(s) to enable Generate.`
                : undefined
          }
        >
          {atQuota ? "Daily limit reached" : "Generate"}
        </Button>
      </>
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      dismissOnBackdropClick={false}
      title="✨ Help me write this prompt"
      description="Describe the report you want and Claude will draft a prompt body for you. Iterate as many times as you'd like before accepting."
      size="lg"
      footer={footer}
    >
      {/* ---------------- Empty state ---------------- */}
      {modalState === "empty" && (
        <div className="space-y-space-3">
          <label
            htmlFor="prompt-builder-desc"
            className="block font-display text-label uppercase tracking-label text-onyx"
          >
            {descriptionLabel}
          </label>
          <Textarea
            id="prompt-builder-desc"
            autoFocus
            rows={6}
            placeholder="e.g., A quarterly QA audit report that reviews a specific loan, identifies process and documentation gaps, rates overall file quality, and produces actionable recommendations for the underwriting team."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <p className="text-caption text-gray-mid">
            Tip: Be specific about the audience, the data sources, and what
            you want the report to conclude or recommend.
          </p>
          <CharCountHint
            current={description.trim().length}
            min={MIN_DESCRIPTION_CHARS}
          />
          {errorMessage && (
            <p
              role="alert"
              className="text-caption text-status-error border border-status-error/40 bg-status-error/5 rounded-md p-space-2"
            >
              {errorMessage}
            </p>
          )}
        </div>
      )}

      {/* ---------------- Generating state ---------------- */}
      {modalState === "generating" && (
        <div className="space-y-space-3">
          <ReadOnlyEcho label="Your description" value={description} />
          {refinementHistory.length > 0 && (
            <p className="text-caption text-gray-mid">
              Refinement {refinementHistory.length} of {SOFT_REFINEMENT_LIMIT_HINT}
            </p>
          )}
          <div className="flex items-center gap-space-2 p-space-4 border border-border-light rounded-md bg-white-warm">
            <span
              className="h-5 w-5 animate-spin rounded-pill border-2 border-gold border-t-transparent"
              aria-hidden
            />
            <span
              className="text-small text-onyx"
              role="status"
              aria-live="polite"
            >
              {GENERATING_STATUS_MESSAGES[statusIndex]}
            </span>
          </div>
        </div>
      )}

      {/* ---------------- Result state ---------------- */}
      {modalState === "result" && lastResult && (
        <div className="space-y-space-3">
          <ReadOnlyEcho label="Your description" value={description} />

          {refinementHistory.length > 0 && (
            <p className="text-caption text-gray-mid">
              Refinement {refinementHistory.length} of {SOFT_REFINEMENT_LIMIT_HINT}
            </p>
          )}

          {quotaBanner && (
            <div
              role="status"
              className="flex items-start gap-space-2 p-space-2 rounded-md border border-status-warning bg-status-warning/10 text-small text-status-warning"
            >
              <AlertTriangle size={14} aria-hidden className="mt-[2px] shrink-0" />
              <span>{quotaBanner.message}</span>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-space-1">
              <label
                htmlFor="prompt-builder-draft"
                className="font-display text-label uppercase tracking-label text-onyx"
              >
                Drafted prompt
              </label>
              <span className="text-caption text-gray-mid">
                ~${lastResult.estimatedCostUsd.toFixed(4)} generated this draft
              </span>
            </div>
            <Textarea
              id="prompt-builder-draft"
              readOnly
              rows={14}
              value={lastResult.draftedPromptBody}
              className="font-mono text-caption"
            />
          </div>

          {lastResult.warnings.length > 0 && (
            <ul className="space-y-space-1 p-space-2 rounded-md border border-status-warning/40 bg-status-warning/10">
              {lastResult.warnings.map((w, i) => (
                <li
                  key={i}
                  className="flex items-start gap-space-2 text-caption text-status-warning"
                >
                  <AlertTriangle size={14} aria-hidden className="mt-[2px] shrink-0" />
                  <span>{w}</span>
                </li>
              ))}
            </ul>
          )}

          {errorMessage && (
            <p
              role="alert"
              className="text-caption text-status-error border border-status-error/40 bg-status-error/5 rounded-md p-space-2"
            >
              {errorMessage}
            </p>
          )}

          <div>
            <label
              htmlFor="prompt-builder-refine"
              className="block font-display text-label uppercase tracking-label text-onyx mb-space-1"
            >
              How does it look?
            </label>
            <Textarea
              id="prompt-builder-refine"
              rows={3}
              placeholder="Tell me how to revise — make it shorter, focus more on cash flow analysis, use a more formal tone…"
              value={refineFeedback}
              onChange={(e) => setRefineFeedback(e.target.value)}
              disabled={atQuota}
            />
          </div>
        </div>
      )}
    </Modal>
  );
}

// -----------------------------------------------------------------------
// Internal helpers
// -----------------------------------------------------------------------

function CharCountHint({ current, min }: { current: number; min: number }) {
  if (current >= min) {
    return (
      <p className="text-caption text-gray-mid">
        {current} character{current === 1 ? "" : "s"} — ready to generate.
      </p>
    );
  }
  return (
    <p className="text-caption text-gray-mid">
      {current}/{min} characters — add a bit more detail before generating.
    </p>
  );
}

function ReadOnlyEcho({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="font-display text-label uppercase tracking-label text-gray-mid mb-space-1">
        {label}
      </div>
      <p className="text-caption text-gray-mid italic whitespace-pre-wrap">
        {value}
      </p>
    </div>
  );
}

/**
 * Renders "X drafts used today · Y remaining" in the modal footer. Sits
 * to the left of the action buttons via flex auto-margin so the footer's
 * right-aligned button bar isn't disturbed.
 */
function QuotaIndicator({
  draftsUsedToday,
  draftsRemaining,
}: {
  draftsUsedToday: number | null;
  draftsRemaining: number | null;
}) {
  if (draftsUsedToday === null || draftsRemaining === null) {
    return <span className="mr-auto text-caption text-gray-mid">&nbsp;</span>;
  }
  return (
    <span className="mr-auto text-caption text-gray-mid">
      {draftsUsedToday} draft{draftsUsedToday === 1 ? "" : "s"} used today ·{" "}
      {draftsRemaining} remaining
    </span>
  );
}

// -----------------------------------------------------------------------
// Trigger button — exported alongside the modal so the Basics editor can
// drop it next to the Prompt Body label without rebuilding the auth +
// state-management plumbing inline.
// -----------------------------------------------------------------------

interface PromptBuilderButtonProps {
  templateId: string | null;
  form: UpsertReportTemplatePayload;
  onAcceptDraft: (draftedPromptBody: string) => void;
}

/**
 * Renders the "✨ Help me write this" trigger and owns the modal open
 * state. Hidden entirely (returns null) when the current user lacks
 * Reports.DraftPrompts — never disabled. Lives as a sibling of the
 * parent Prompt Body label.
 */
export function PromptBuilderButton({
  templateId,
  form,
  onAcceptDraft,
}: PromptBuilderButtonProps) {
  const { hasPermission } = useAuth();
  const [open, setOpen] = useState(false);

  if (!hasPermission("Reports.DraftPrompts")) {
    return null;
  }

  return (
    <>
      <Button
        variant="secondary"
        size="sm"
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Help me write this prompt with AI"
      >
        <Sparkles size={14} className="text-gold" aria-hidden /> Help me write this
      </Button>
      {open && (
        <PromptBuilderModal
          templateId={templateId}
          form={form}
          onAcceptDraft={onAcceptDraft}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
